import { ContactStore } from './ContactStore';
import { DealStore } from './DealStore';
import { DealTypeStore } from './DealTypeStore';
import { ProjectStore } from './ProjectStore';
import { TaskStore } from './TaskStore';
import { NoteStore } from './NoteStore';
import { NoteAttachmentStore } from './NoteAttachmentStore';
import { AuditLogStore } from './AuditLogStore';
import { UserStore } from './UserStore';
import { SyncStore } from './SyncStore';
import type { CrmDatabase } from '../lib/db/database';
import type { EntityType } from './types';

export class RootStore {
  contactStore: ContactStore;
  dealStore: DealStore;
  dealTypeStore: DealTypeStore;
  projectStore: ProjectStore;
  taskStore: TaskStore;
  noteStore: NoteStore;
  noteAttachmentStore: NoteAttachmentStore;
  auditLogStore: AuditLogStore;
  userStore: UserStore;
  syncStore: SyncStore;

  constructor() {
    this.contactStore = new ContactStore(this);
    this.dealStore = new DealStore(this);
    this.dealTypeStore = new DealTypeStore(this);
    this.projectStore = new ProjectStore(this);
    this.taskStore = new TaskStore(this);
    this.noteStore = new NoteStore(this);
    this.noteAttachmentStore = new NoteAttachmentStore();
    this.auditLogStore = new AuditLogStore();
    this.userStore = new UserStore();
    this.syncStore = new SyncStore();
  }

  async loadFromDatabase(db: CrmDatabase): Promise<void> {
    this.syncStore.setDb(db);

    // Load small metadata stores eagerly — these unlock the UI (dropdowns, lookups)
    const [dealTypes, projects, users, meta, pendingMutations] = await Promise.all([
      db.getAll('dealTypes'),
      db.getAll('projects'),
      db.getAll('users'),
      db.getSyncMeta(),
      db.getAllPendingMutations(),
    ]);

    this.dealTypeStore.loadFromRecords(dealTypes);
    this.projectStore.loadFromRecords(projects);
    this.userStore.loadFromRecords(users);
    this.syncStore.pendingMutationCount = pendingMutations.length;

    if (meta) {
      this.syncStore.setSyncVersion(meta.lastSyncVersion);
      this.syncStore.setMetadataReady(true);
      if (meta.bootstrapComplete) {
        this.syncStore.serverBootstrapDone = true;
      }
    }

    // Load large stores (contacts, deals, tasks) in the background.
    // bootstrapComplete is set only after these finish so list views
    // correctly show their loading state until data is ready.
    this._hydrateLargeStoresFromDb(db, meta?.bootstrapComplete ?? false);
  }

  private async _hydrateLargeStoresFromDb(db: CrmDatabase, wasBootstrapped: boolean): Promise<void> {
    // Read all stores in a single IDB transaction. Combining reads avoids
    // paying the IDB cold-start cost (database file loaded from disk on the
    // first transaction after page reload) more than once.
    //
    // NOTE: In dev mode (Turbopack), IDB event delivery is delayed by main-
    // thread contention from module compilation, inflating this read to ~8s.
    // In production the same read completes in ~80ms. This is a known
    // dev-mode artifact — not an IDB or app-level performance issue.
    const data = await db.getAllMultiple([
      'contacts', 'deals', 'tasks', 'notes', 'noteAttachments', 'auditLogs'
    ] as const);

    // Hydrate list-view-essential stores first so the UI unblocks quickly
    this.contactStore.loadFromRecords(data.contacts);
    this.dealStore.loadFromRecords(data.deals);
    this.taskStore.loadFromRecords(data.tasks);

    // Mark complete so entity hooks stop showing skeletons. When online and not
    // yet bootstrapped, we wait for bootstrap() to fill the stores before setting
    // this flag, giving a correct "loading" state. When offline (or already
    // bootstrapped from a prior session), set it immediately — IDB data (or
    // empty stores) IS the correct state; skeletons would spin forever otherwise.
    if (wasBootstrapped || !navigator.onLine) {
      this.syncStore.setBootstrapComplete(true);
    }

    // Yield to the macrotask queue so React can paint list views before
    // we load the heavier secondary stores (notes, auditLogs) that are
    // only consumed on detail/timeline pages.
    await new Promise(resolve => setTimeout(resolve, 0));

    this.noteStore.loadFromRecords(data.notes);
    this.noteAttachmentStore.loadFromRecords(data.noteAttachments);
    this.auditLogStore.loadFromRecords(data.auditLogs);
  }

  getStoreForEntity(entity: EntityType) {
    switch (entity) {
      case 'contact': return this.contactStore;
      case 'deal': return this.dealStore;
      case 'dealType': return this.dealTypeStore;
      case 'project': return this.projectStore;
      case 'task': return this.taskStore;
      case 'note': return this.noteStore;
      case 'noteAttachment': return this.noteAttachmentStore;
      case 'auditLog': return this.auditLogStore;
      case 'user': return this.userStore;
      default: return null;
    }
  }

  clearAll(): void {
    this.contactStore.clear();
    this.dealStore.clear();
    this.dealTypeStore.clear();
    this.projectStore.clear();
    this.taskStore.clear();
    this.noteStore.clear();
    this.noteAttachmentStore.clear();
    this.auditLogStore.clear();
    this.userStore.clear();
    this.syncStore.clear();
  }
}
